import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Home, Calendar } from "lucide-react";
import axios from "axios";
import { trackPurchase } from "@/utils/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, pending_confirmation, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [tracked, setTracked] = useState(false);
  const sessionIdParam = searchParams.get("session_id") || "";

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkPaymentStatus = async (attempts = 0) => {
      // Poll for up to 60s (30 attempts × 2s). Square usually transitions the
      // order to COMPLETED within a few seconds of the payment, but it can lag
      // during traffic spikes. If we still see "pending" after the polling
      // window, we show a friendly "we're finalising" state — the webhook +
      // background watchdog will confirm shortly and email the customer.
      const maxAttempts = 30;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("pending_confirmation");
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);

        if (res.data.payment_status === "paid") {
          setPaymentDetails(res.data);
          setStatus("success");

          // Track purchase with Meta Pixel (only once)
          if (!tracked) {
            const amount = res.data.amount_total ? res.data.amount_total / 100 : 0;
            trackPurchase(amount, res.data.currency?.toUpperCase() || 'AUD', [sessionId]);
            setTracked(true);
          }
          return;
        } else if (res.data.status === "expired") {
          setStatus("error");
          return;
        }

        // Continue polling
        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus("pending_confirmation");
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  return (
    <div className="pt-20 min-h-screen bg-dream-bg flex items-center justify-center" data-testid="booking-success-page">
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        {status === "loading" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl p-12"
            data-testid="loading-state"
          >
            <Loader2 className="w-16 h-16 text-dream-violet mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Processing Payment
            </h1>
            <p className="text-white/60">
              Please wait while we confirm your payment...
            </p>
          </motion.div>
        )}

        {status === "success" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl p-12"
            data-testid="success-state"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              Booking Confirmed!
            </h1>
            <p className="text-white/60 mb-8">
              Thank you for booking with Dream Makers Recording Group. 
              A confirmation email will be sent to your inbox shortly.
            </p>
            
            {paymentDetails && (
              <div className="bg-dream-surface rounded-xl p-6 border border-dream-border mb-8 text-left">
                <h3 className="font-semibold text-white mb-3">Payment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/60">Amount Paid</span>
                    <span className="font-mono text-dream-violet">
                      ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/60">Status</span>
                    <span className="text-green-500">Paid</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button 
                  variant="outline"
                  className="border-dream-border text-white hover:border-dream-violet hover:text-dream-violet bg-transparent w-full sm:w-auto"
                >
                  <Home className="mr-2 w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/booking">
                <Button className="bg-dream-violet text-white hover:bg-dream-violet-glow w-full sm:w-auto">
                  <Calendar className="mr-2 w-4 h-4" />
                  Book Another Session
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {status === "pending_confirmation" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl p-12"
            data-testid="pending-state"
          >
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-yellow-400" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-3">
              Payment Received — Finalising Booking
            </h1>
            <p className="text-white/70 mb-4">
              Thanks! Your payment went through. We&apos;re just finalising your booking on our end —
              you&apos;ll get a confirmation email within a few minutes.
            </p>
            <p className="text-white/50 text-sm mb-8">
              If you don&apos;t see it in 15 minutes (check spam too), reply to your booking-started email
              or call us on <a href="tel:0421815593" className="text-dream-violet hover:underline">0421 815 593</a>
              {sessionIdParam ? <> and quote booking reference <span className="font-mono text-white/80">{sessionIdParam.slice(0, 8)}</span>.</> : "."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button
                  variant="outline"
                  className="border-dream-border text-white hover:border-dream-violet hover:text-dream-violet bg-transparent w-full sm:w-auto"
                >
                  <Home className="mr-2 w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-dream-violet text-white hover:bg-dream-violet-glow w-full sm:w-auto">
                  Contact Support
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {status === "error" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl p-12"
            data-testid="error-state"
          >
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white mb-2">
              Payment Issue
            </h1>
            <p className="text-white/60 mb-8">
              We couldn&apos;t confirm your payment. This could be due to a timeout or 
              cancellation. Please try booking again or contact us for assistance.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button className="bg-dream-violet text-white hover:bg-dream-violet-glow w-full sm:w-auto">
                  Try Again
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="border-dream-border text-white hover:border-dream-violet hover:text-dream-violet bg-transparent w-full sm:w-auto"
                >
                  Contact Support
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
